import apiClient from "../lib/api-client";

export interface DrugLicense {
  id: string;
  storeId: string;
  licenseNumber: string;
  licenseType: string;
  issuingAuthority: string;
  issueDate: string;
  expiryDate: string;
  status: DrugLicenseStatus;
  schedulesCovered: string[];
  documents: string[];
  lastRenewalDate?: string | null;
  nextRenewalDue?: string | null;
  registeredPharmacist?: string;
  pharmacistRegNumber?: string;
  documentUrl?: string;
  notes?: string;
  isActive: boolean;
  store?: {
    id: string;
    name: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface CreateDrugLicenseData {
  storeId: string;
  licenseNumber: string;
  licenseType: string;
  issuingAuthority: string;
  issueDate: string;
  expiryDate: string;
  registeredPharmacist?: string;
  pharmacistRegNumber?: string;
  schedulesCovered?: string[];
  documents?: string[];
  documentUrl?: string;
  notes?: string;
}

export type DrugLicenseStatus =
  | "ACTIVE"
  | "EXPIRED"
  | "SUSPENDED"
  | "CANCELLED"
  | "PENDING_RENEWAL";

export type UpdateDrugLicenseData = Partial<CreateDrugLicenseData> & {
  status?: DrugLicenseStatus;
  isActive?: boolean;
};

export interface DrugLicensesParams {
  page?: number;
  limit?: number;
  search?: string;
  storeId?: string;
  licenseType?: string;
  status?: DrugLicenseStatus;
  expiringDays?: number;
}

export interface DrugLicensesResponse {
  data: DrugLicense[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

function mutationKey(storageKey: string): string {
  const existing = sessionStorage.getItem(storageKey);
  if (existing) return existing;
  const key = crypto.randomUUID();
  sessionStorage.setItem(storageKey, key);
  return key;
}

export const drugLicenseService = {
  getAll: async (
    params?: DrugLicensesParams,
  ): Promise<DrugLicensesResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", params.page.toString());
    if (params?.limit) searchParams.set("limit", params.limit.toString());
    if (params?.search) searchParams.set("search", params.search);
    if (params?.storeId) searchParams.set("storeId", params.storeId);
    if (params?.licenseType)
      searchParams.set("licenseType", params.licenseType);
    if (params?.status) searchParams.set("status", params.status);
    if (params?.expiringDays)
      searchParams.set("expiringDays", params.expiringDays.toString());

    const response = await apiClient.get(
      `/drug-licenses?${searchParams.toString()}`,
    );
    return response.data;
  },

  getById: async (id: string): Promise<DrugLicense> => {
    const response = await apiClient.get(`/drug-licenses/${id}`);
    return response.data;
  },

  getByStore: async (storeId: string): Promise<DrugLicense[]> => {
    const response = await apiClient.get(`/drug-licenses/by-store/${storeId}`);
    return response.data;
  },

  getExpiring: async (
    days?: number,
    storeId?: string,
  ): Promise<DrugLicense[]> => {
    const searchParams = new URLSearchParams();
    if (days) searchParams.set("days", days.toString());
    if (storeId) searchParams.set("storeId", storeId);
    const response = await apiClient.get(
      `/drug-licenses/expiring?${searchParams.toString()}`,
    );
    return response.data;
  },

  getLicenseTypes: async (): Promise<string[]> => {
    const response = await apiClient.get("/drug-licenses/types");
    return response.data;
  },

  create: async (data: CreateDrugLicenseData): Promise<DrugLicense> => {
    const storageKey = `medicorepay:drug-license:create:${data.storeId}:${data.licenseNumber}`;
    const response = await apiClient.post("/drug-licenses", data, {
      headers: { "Idempotency-Key": mutationKey(storageKey) },
    });
    sessionStorage.removeItem(storageKey);
    return response.data;
  },

  update: async (
    id: string,
    data: UpdateDrugLicenseData,
  ): Promise<DrugLicense> => {
    const storageKey = `medicorepay:drug-license:update:${id}`;
    const response = await apiClient.patch(`/drug-licenses/${id}`, data, {
      headers: { "Idempotency-Key": mutationKey(storageKey) },
    });
    sessionStorage.removeItem(storageKey);
    return response.data;
  },

  renew: async (
    id: string,
    newExpiryDate: string,
    renewalDate?: string,
  ): Promise<DrugLicense> => {
    const storageKey = `medicorepay:drug-license:renew:${id}`;
    const response = await apiClient.post(
      `/drug-licenses/${id}/renew`,
      { newExpiryDate, renewalDate },
      { headers: { "Idempotency-Key": mutationKey(storageKey) } },
    );
    sessionStorage.removeItem(storageKey);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    const storageKey = `medicorepay:drug-license:delete:${id}`;
    await apiClient.delete(`/drug-licenses/${id}`, {
      headers: { "Idempotency-Key": mutationKey(storageKey) },
    });
    sessionStorage.removeItem(storageKey);
  },
};
